import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import StorageIcon from '@mui/icons-material/Storage';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ConstructionIcon from '@mui/icons-material/Construction';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const STEP_ICONS = [
  <StorageIcon sx={{ fontSize: '1.9rem' }} />,
  <AutoFixHighIcon sx={{ fontSize: '1.9rem' }} />,
  <ConstructionIcon sx={{ fontSize: '1.9rem' }} />,
  <RocketLaunchIcon sx={{ fontSize: '1.9rem' }} />,
];

const OnePlatform: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const steps = r.oneplatform.steps;

  return (
    <Box component="section" id="platform" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.8rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
            }}
          >
            {t('oneplatform.title')}
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 7, color: '#64748B', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}
          >
            {t('oneplatform.subtitle')}
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 3, md: 3 },
            position: 'relative',
            mb: 4,
          }}
        >
          {steps.map((step, idx) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.15 * idx }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  p: 3.5,
                  borderRadius: 3,
                  background: 'white',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: '#CBD5E1',
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: '#0F172A',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                  }}
                >
                  {STEP_ICONS[idx]}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    color: '#64748B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    letterSpacing: '0.06em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  0{idx + 1}
                </Typography>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 700, color: '#0F172A', mb: 1.5, fontSize: '1.3rem', letterSpacing: '-0.01em' }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {step.description}
                </Typography>
                {idx < steps.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      position: 'absolute',
                      top: '50%',
                      right: -22,
                      transform: 'translateY(-50%)',
                      color: '#CBD5E1',
                      zIndex: 1,
                    }}
                  >
                    <ArrowForwardIcon />
                  </Box>
                )}
              </Box>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Typography
            align="center"
            sx={{
              color: '#475569',
              fontWeight: 500,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              mt: 3,
            }}
          >
            {t('oneplatform.closing')}
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
};

export default OnePlatform;
